import { NetworkPointResource } from 'app/_resources/network-point.resource';
import * as angular from 'angular';
import * as moment from 'pkp-moment-timezone-all-years';
import { Growl } from 'angular/custom/services/growl.service';
import { config } from 'angular/utils/config';

export class CapacitySurrenderCtrl implements ng.IController {
    public uiLoadingNetworkPoint = true;
    public uiErrorLoadingNetworkPoint = false;
    public uiCheckPlausibilityError = false;
    public uiSubmitConfirmationSuccess = false;
    public uiSubmitConfirmationError = false;
    public loadingUnderlyingDeals = true;
    public isConfigFetched = false;
    public id;
    public disableSubmit = false;
    public hasValidDealId = false;
    public previousRuntimeStart = null;
    public stageConfig = config;

    /**
     * The flag indicates whether it is possible to surrender capacity on a given NP. IN particular for:
     * Unbundled NP(s): the surrender is possible if the configuration of the (only) TSO on the NP is enabled;
     * Bundled NP(s): the surrender is possible if the configuration of the at lest one of the TSOs on the NP is enabled;
     *
     * @type {boolean}
     */
    public isSurrenderPossible = false;
    /**
     * The <tt>Surrender.checkPlausibility</tt> service returns an error array that is assigned to this node.
     *
     * @type {Array}
     */
    public checkPlausibilityErrorMessages = [];
    /**
     * Fake validator state attribute written by the <tt>pkpRuntimeValidator</tt> directive.
     *
     * @type {boolean}
     */
    public isValidRuntime = true;
    /**
     * Field validator state attribute to manage the check against the minimum surrender quantity configuration field.
     *
     * @type {boolean}
     */
    public isGtMinQty = true;
    /**
     * Field validator state attribute to manage the check against the minimum lead time.
     *
     * @type {boolean}
     */
    public isValidLeadTime = true;
    /**
     * Field validator state attribute to manage the check against the month duration.
     *
     * @type {boolean}
     */
    public isMultipleOfMonth = true;
    /**
     * The network point to surrender capacity on.
     *
     * @type {Object}
     */
    public networkPoint: NetworkPointDto = null;
    /**
     * The configuration data from the network point subject to surrender.
     *
     * @type {Object}
     */
    public configurationData: SurrenderConfigDto = {} as any;
    /**
     * Either <tt>['0', '1']</tt> or <tt>['0']</tt> to iterate through tso0 and tso1.
     * @type {Array}
     */
    public tsoRange = [];
    /**
     * Checkbox that will disable booking id input if true.
     *
     * @type {boolean}
     */
    public noBookingId = false;
    /**
     * The model that should be submitted to the endpoint as is.
     *
     * @type {Object}
     */
    public surrender: SurrenderCapacityDto;
    /**
     * Contains the activity from the succeeded surrender request.
     *
     * @type {Array}
     */
    public activities = [];
    public dealIds = [];

    public submitConfirmationErrorMessage: string;
    public checkPlausibilityErrorMessage: string;

    public errorTexts = {
        requestedAmountIsBeneathTheMinimumSizeOfTheRespectiveTSO:
            'The requested amount is beneath the minimum size of the respective TSO.',
        deadlineForReturningExpired:
            'The deadline for returning this capacity has already expired.',
        transportPeriodMultipleOfMonth:
            'The transport period has to be multiple of a month.'
    };

    constructor(
        private $window: ng.IWindowService,
        private $timeout: ng.ITimeoutService,
        private $scope: ng.IScope,
        private $routeParams: ng.route.IRouteParamsService,
        private $route: ng.route.IRouteService,
        private NetworkPoint: NetworkPointResource,
        private Surrender,
        private activitiesService,
        public INCLUDE,
        private growl: Growl
    ) {
        this.id = $routeParams.networkPointId;
        this.surrender = {
            quantity: { unit: 'kWh/h' },
            interval: {},
            sourceNodeRef: this.$routeParams.networkPointId
        } as any;

        this.setDealIdsForSurrender();

        NetworkPoint.get(
            { id: $routeParams.networkPointId },
            (res) => {
                if (!res.id) {
                    this.uiErrorLoadingNetworkPoint = true;
                    return;
                }
                this.networkPoint = res;
                this.tsoRange = this.networkPoint.bundled ? ['0', '1'] : ['0'];
            },
            () => {
                this.uiErrorLoadingNetworkPoint = true;
            }
        ).$promise.finally(() => {
            this.uiLoadingNetworkPoint = false;
        });

        this.$scope.$watchCollection(
            (_) => {
                return this.surrender.interval;
            },
            () => {
                const interval = this.surrender.interval;

                if (!interval) {
                    return;
                }
                const start = interval.start;
                const end = interval.end;

                if (!!start) {
                    if (
                        this.previousRuntimeStart == null ||
                        start.valueOf() !== this.previousRuntimeStart.valueOf()
                    ) {
                        this.getSurrenderConfig(start).then(() => {
                            if (
                                !angular.isUndefined(start) &&
                                !angular.isUndefined(end)
                            ) {
                                this.isValidLeadTime = this.checkLeadTime(
                                    interval,
                                    this.configurationData
                                        .minimumLeadTimeInDays,
                                    this.configurationData
                                        .timeForMinimumLeadTime
                                );

                                this.isMultipleOfMonth =
                                    this.checkIsMultipleOfMonth(
                                        interval,
                                        this.configurationData.multipleMonth
                                    );
                            } else {
                                // If the data on the UI are not yet set, we do not throw any error.
                                this.isMultipleOfMonth = true;
                            }
                        });
                        this.previousRuntimeStart = start;
                    }
                } else {
                    // If the data on the UI are reset, we delete the configuration and reset all the parameters.
                    this.configurationData = {} as any;
                    this.isConfigFetched = false;
                    this.isValidLeadTime = true;
                    this.disableSubmit = true;
                    this.isMultipleOfMonth = true;
                }
            }
        );
    }

    /**
     * Surrender the capacity.
     *
     * @param {boolean} isValid
     * @returns {boolean} True if a call to an endpoint was made, false otherwise.
     */
    public submitSurrenderCapacity(form: ng.IFormController) {
        if (
            this.disableSubmit ||
            !form.$valid ||
            !this.isValidRuntime ||
            !this.isValidLeadTime ||
            !this.isMultipleOfMonth ||
            !this.isGtMinQty
        ) {
            const extraErrorMessages: { field: string; message: string }[] = [];
            if (!this.isGtMinQty) {
                extraErrorMessages.push({
                    field: 'Capacity*',
                    message:
                        this.errorTexts
                            .requestedAmountIsBeneathTheMinimumSizeOfTheRespectiveTSO
                });
            }
            if (!this.isValidLeadTime) {
                extraErrorMessages.push({
                    field: 'Period to return*',
                    message: this.errorTexts.deadlineForReturningExpired
                });
            }
            if (!this.isMultipleOfMonth) {
                extraErrorMessages.push({
                    field: 'Period to return*',
                    message: this.errorTexts.transportPeriodMultipleOfMonth
                });
            }
            return false;
        }

        this.disableSubmit = true;
        this.uiCheckPlausibilityError = false;
        let surrenderDto = angular.copy(this.surrender);
        if (this.noBookingId) {
            surrenderDto.dealId = null;
        }
        this.Surrender.checkPlausibility(
            surrenderDto,
            (res) => {
                if (res.errors.length > 0 || !res.success) {
                    this.checkPlausibilityErrorMessages = res.errors;
                    this.disableSubmit = false;
                    return;
                }

                this.$timeout(() => {
                    this.Surrender.submitConfirmation(
                        res,
                        () => {
                            this.uiSubmitConfirmationSuccess = true;
                            const activities =
                                this.activitiesService.convertSurrenderToActivity(
                                    res,
                                    this.networkPoint
                                );
                            this.activities =
                                this.activitiesService.map(
                                    activities
                                ).activities;
                        },
                        (err) => {
                            this.uiSubmitConfirmationError = true;
                            this.submitConfirmationErrorMessage =
                                err.data.message;
                            this.disableSubmit = false;
                        }
                    );
                }, 2500);
            },
            (err) => {
                this.uiCheckPlausibilityError = true;
                this.checkPlausibilityErrorMessage = err.data.message;
                this.disableSubmit = false;
            }
        );

        return true;
    }

    public getSurrenderConfig(start) {
        return this.Surrender.getConfig(
            {
                id: this.id,
                runtimeStart: start
            },
            (res) => {
                this.configurationData = res;
                this.isConfigFetched = true;
                this.disableSubmit = false;
                this.isSurrenderPossible =
                    this.configurationData.surrenderSupport !== 'NOT_SUPPORTED';

                if (
                    this.configurationData.surrenderSupport === 'NOT_SUPPORTED'
                ) {
                    const message =
                        'Please notice that the platform will not support the surrender of capacities at this interconnection point.' +
                        ' Please contact the involved TSO for surrender of capacity contracts at this point.';
                    this.growl.show(message, 'danger');
                }
                if (this.configurationData.surrenderSupport === 'PARTIAL') {
                    const message =
                        'Please notice that the platform will only support the surrender of capacities for ' +
                        '<strong>one TSO</strong> at this interconnection point. Please contact the second TSO for surrender' +
                        ' of capacity contracts at this point.';
                    this.growl.show(message, 'danger');
                }
            },
            () => {}
        ).$promise;
    }

    public setDealIdsForSurrender() {
        this.Surrender.getDealIds(
            {
                networkPointId: this.id
            },
            (res) => {
                this.loadingUnderlyingDeals = false;
                this.dealIds = res;
            },
            () => {} // no problem if no deals are found, manual entry is possible
        );
    }

    public filterByDealIdPrefix = function (deal, prefix) {
        if (deal.dealId) {
            var dealId = deal.dealId;
            if (dealId.length < prefix.length) {
                return false;
            }
            for (
                var i = prefix.length - 1;
                i >= 0 && dealId[i] === prefix[i];
                --i
            ) {
                continue;
            }
            return i < 0;
        }
    };

    public onSelect = function ($item) {
        this.initFieldsFromDeal($item.dealId);
    };

    public checkLeadTime(interval, leadTimeDaysBefore, leadTimeTimeOfDay) {
        if (typeof interval !== 'undefined') {
            if (
                angular.isUndefined(leadTimeDaysBefore) ||
                angular.isUndefined(leadTimeTimeOfDay)
            ) {
                this.growl.show('Whoops! An unknown error occurred.', 'danger');
                return false;
            }

            const convertedLeadTime = moment.duration(leadTimeTimeOfDay);
            if (
                parseInt(leadTimeDaysBefore) < 0 ||
                convertedLeadTime.hours() < 0 ||
                convertedLeadTime.hours() >= 24 ||
                leadTimeDaysBefore <= 0
            ) {
                this.growl.show('Whoops! An unknown error occurred.', 'danger');
                return false;
            }

            const now = moment();
            let lastPossibleBooking = moment(interval.start).subtract(
                parseInt(leadTimeDaysBefore),
                'days'
            );
            lastPossibleBooking = lastPossibleBooking.set({
                hour: convertedLeadTime.hours(),
                minute: convertedLeadTime.minutes()
            });

            return now.isBefore(lastPossibleBooking);
        }
    }

    public checkIsMultipleOfMonth(interval, isMultipleMonth) {
        if (
            typeof interval !== 'undefined' &&
            typeof isMultipleMonth !== 'undefined' &&
            isMultipleMonth
        ) {
            const start = moment(interval.start);
            const end = moment(interval.end);

            const year0 = start.year();
            const year1 = end.year();

            const month0 = start.month();
            const month1 = end.month();

            const day0 = start.date();
            const day1 = end.date();
            return (
                day0 === 1 &&
                day1 === 1 &&
                (month1 - month0 !== 0 || year1 - year0 !== 0)
            );
        }
        // If the data provided by the UI are not valid or the TSO does not require a period to be
        // multiple of a month, we do not throw any error.
        return true;
    }

    public checkCapacityMinQuantity(reqQty, minQty) {
        if (
            typeof reqQty.value !== 'undefined' &&
            typeof minQty !== 'undefined'
        ) {
            this.isGtMinQty = reqQty.value.value >= minQty;
        }
    }

    public getDeal(dealId) {
        return this.dealIds.find((value) => value.dealId === dealId);
    }

    public isInDealIds(dealId) {
        return !!this.getDeal(dealId);
    }

    public initFieldsFromDeal(dealId) {
        let deal = this.getDeal(dealId);
        if (deal) {
            this.surrender.dealId = deal.dealId;
            this.surrender.interval = deal.runtime;
            this.surrender.quantity = deal.amount;
            if (this.networkPoint.bundled) {
                this.surrender.contractPositionId0 = deal.contractNumber0;
                this.surrender.accountingGridId0 = deal.balancingGroup0;
                this.surrender.portfolioCode0 = deal.portfolioCode0;
                this.surrender.contractPositionId1 = deal.contractNumber1;
                this.surrender.accountingGridId1 = deal.balancingGroup1;
                this.surrender.portfolioCode1 = deal.portfolioCode1;
            } else {
                this.surrender.contractPositionId0 = deal.contractNumber0;
                this.surrender.accountingGridId0 = deal.balancingGroup0;
                this.surrender.portfolioCode0 = deal.portfolioCode0;
            }
        }
    }

    public goBack() {
        this.$window.history.back();
    }

    public reloadRoute() {
        this.$route.reload();
    }
}

angular
    .module('app')
    .controller('CapacitySurrenderCtrl', CapacitySurrenderCtrl);
